import { axiosInstance } from '../config/axiosConfig';
import { API_ENDPOINTS } from '../endpoints/endpoints';

export interface BulkUploadJob {
  jobId: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'PARTIAL' | 'FAILED';
  totalRows: number;
  processedRows: number;
  successCount: number;
  failureCount: number;
  createdByAdmin: boolean;
  targetDealerId?: string;
  startedAt?: string;
  completedAt?: string;
  results?: JobRowResult[];
}

export interface JobRowResult {
  row: number;
  status: 'success' | 'failed' | 'skipped';
  listingId?: string;
  make?: string;
  model?: string;
  year?: number;
  imageCount?: number;
  reason?: string;
}

export interface CsvParseResult {
  jobId: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  csvErrors: Array<{ row: number; errors: string[] }>;
  preview: CsvRow[];
}

export interface CsvRow {
  rowIndex: number;
  make: string;
  model: string;
  year: number;
  price: number;
  mileage?: number;
  color?: string;
  fuel_type?: string;
  transmission?: string;
  body_type?: string;
  condition?: string;
  description?: string;
  title?: string;
  is_negotiable?: boolean;
  location?: string;
  city?: string;
  country?: string;
  doors?: number;
  seats?: number;
  features?: string[];
  _errors: string[];
}

export interface UploadedImage {
  id: string;
  filename: string;
  url: string;
  thumbnailUrl?: string;
  smallUrl?: string;
  size: number;
  format: string;
}

export interface Dealer {
  id: string;
  companyName: string;
  email: string;
  city?: string;
}

// ─── CSV Template ─────────────────────────────────────────────────────────────

export const downloadCsvTemplate = async (): Promise<void> => {
  const response = await axiosInstance.get(API_ENDPOINTS.BULK_UPLOAD.CSV_TEMPLATE, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'autoplace-bulk-upload-template.csv';
  link.click();
  URL.revokeObjectURL(url);
};

// ─── Parse CSV ────────────────────────────────────────────────────────────────

export const parseCsv = async (
  file: File,
  dealerId?: string,
): Promise<CsvParseResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const url = dealerId
    ? `${API_ENDPOINTS.BULK_UPLOAD.PARSE_CSV}?dealerId=${dealerId}`
    : API_ENDPOINTS.BULK_UPLOAD.PARSE_CSV;

  const response = await axiosInstance.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
  return response.data.data;
};

// ─── Upload Images ─────────────────────────────────────────────────────────────

export const uploadImages = async (
  jobId: string,
  files: File[],
  onProgress?: (percent: number) => void,
): Promise<UploadedImage[]> => {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));

  const response = await axiosInstance.post(
    API_ENDPOINTS.BULK_UPLOAD.UPLOAD_IMAGES(jobId),
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    },
  );
  return response.data.data.images;
};

// ─── Image Mapping ────────────────────────────────────────────────────────────

export const setImageMapping = async (
  jobId: string,
  imageMapping: Record<string, string[]>,
): Promise<void> => {
  await axiosInstance.post(API_ENDPOINTS.BULK_UPLOAD.SET_IMAGE_MAPPING(jobId), {
    imageMapping,
  });
};

// ─── Start Job ────────────────────────────────────────────────────────────────

export const startJob = async (
  jobId: string,
  imageMapping?: Record<string, string[]>,
): Promise<{ jobId: string; status: string; totalRows: number }> => {
  const response = await axiosInstance.post(API_ENDPOINTS.BULK_UPLOAD.START_JOB(jobId), {
    imageMapping,
  });
  return response.data.data;
};

// ─── Poll Status ──────────────────────────────────────────────────────────────

export const getJobStatus = async (jobId: string): Promise<BulkUploadJob> => {
  const response = await axiosInstance.get(API_ENDPOINTS.BULK_UPLOAD.JOB_STATUS(jobId));
  return response.data.data;
};

export const getMyJobs = async (): Promise<BulkUploadJob[]> => {
  const response = await axiosInstance.get(API_ENDPOINTS.BULK_UPLOAD.MY_JOBS);
  return response.data.data;
};

// ─── Admin: Dealers ───────────────────────────────────────────────────────────

export const getApprovedDealers = async (): Promise<Dealer[]> => {
  const response = await axiosInstance.get(API_ENDPOINTS.BULK_UPLOAD.ADMIN_DEALERS);
  return response.data.data;
};

// ─── Admin: Single Listing ────────────────────────────────────────────────────

export const adminCreateSingleListing = async (
  data: {
    dealerId: string;
    make: string;
    model: string;
    year: number;
    price: number;
    mileage?: number;
    color?: string;
    fuelType?: string;
    transmission?: string;
    bodyType?: string;
    condition?: string;
    description?: string;
    title?: string;
    isNegotiable?: boolean;
    location?: string;
    city?: string;
    country?: string;
    doors?: number;
    seats?: number;
    features?: string;
    displacement?: string;
    driveType?: string;
    numberOfOwners?: number;
  },
  images: File[],
  onProgress?: (percent: number) => void,
): Promise<any> => {
  const formData = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (v !== undefined && v !== null) formData.append(k, String(v));
  });
  images.forEach(img => formData.append('files', img));

  const response = await axiosInstance.post(
    API_ENDPOINTS.BULK_UPLOAD.ADMIN_SINGLE_LISTING,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    },
  );
  return response.data.data;
};

// ─── Admin: upload photos to an existing listing ──────────────────────────────

export const adminUploadListingPhotos = async (
  listingId: string,
  files: File[],
  onProgress?: (pct: number) => void,
): Promise<void> => {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));
  await axiosInstance.post(
    API_ENDPOINTS.LISTINGS.ADMIN_PHOTOS_UPLOAD(listingId),
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    },
  );
};

// ─── Export errors as CSV ─────────────────────────────────────────────────────

export const exportResultsAsCsv = (results: JobRowResult[]): void => {
  const failed = results.filter(r => r.status !== 'success');
  if (failed.length === 0) return;

  const headers = 'Row,Status,Make,Model,Year,Reason';
  const rows = failed.map(r =>
    [r.row, r.status, r.make ?? '', r.model ?? '', r.year ?? '', `"${r.reason ?? ''}"`].join(','),
  );
  const csv = [headers, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'bulk-upload-errors.csv';
  link.click();
  URL.revokeObjectURL(url);
};
